/**
 * Desktop AI Battery Component
 * Independent copy of the mobile AI battery with desktop-specific styling hooks
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_CONFIG } from '../../../config/api';
import useLanguage from '../../../hooks/useLanguage';
import Iridescence from '../../Mobile/MobileBatteryPanel/Iridescence';
import { Skeleton } from '../../effects';
import { useAuthContext } from '../../../context/AuthContext';
import { apiFetch, parseResponseBody } from '../../../utils/http';

const toNumber = (value, fallback = 0) => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const WEEK_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000;

const parseAPITimestamp = (value) => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'number') {
    const asMilliseconds = value > 1e12 ? value : value * 1000;
    const date = new Date(asMilliseconds);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    if (/^\d+$/.test(trimmed)) {
      return parseAPITimestamp(Number(trimmed));
    }

    const hasTimezone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(trimmed);
    const normalized = hasTimezone
      ? trimmed
      : `${trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T')}Z`;

    const date = new Date(normalized);
    if (!Number.isNaN(date.getTime())) {
      return date;
    }

    const fallback = new Date(trimmed.replace(' ', 'T'));
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }

  if (typeof value.toString === 'function') {
    return parseAPITimestamp(value.toString());
  }

  return null;
};

const computeWeeklyCycle = ({
  currentWeekStart,
  currentWeekEnd,
  nextRefreshAt,
  lastRefreshAt
} = {}) => {
  const now = new Date();

  const parsedLastRefresh = parseAPITimestamp(lastRefreshAt) || parseAPITimestamp(currentWeekStart);
  const parsedWeekStart = parseAPITimestamp(currentWeekStart)
    || (parsedLastRefresh ? new Date(parsedLastRefresh) : new Date(now.getTime() - WEEK_INTERVAL_MS));

  const parsedWeekEnd = parseAPITimestamp(currentWeekEnd)
    || (parsedWeekStart ? new Date(parsedWeekStart.getTime() + WEEK_INTERVAL_MS) : new Date(now.getTime() + WEEK_INTERVAL_MS));

  const parsedNextRefresh = parseAPITimestamp(nextRefreshAt)
    || (parsedWeekEnd ? new Date(parsedWeekEnd) : new Date(parsedWeekStart.getTime() + WEEK_INTERVAL_MS));

  const lastRefresh = parsedLastRefresh && !Number.isNaN(parsedLastRefresh.getTime())
    ? parsedLastRefresh
    : new Date(parsedWeekStart.getTime());

  let nextRefresh = parsedNextRefresh && !Number.isNaN(parsedNextRefresh.getTime())
    ? parsedNextRefresh
    : new Date(parsedWeekEnd.getTime());

  if (nextRefresh <= now) {
    nextRefresh = new Date(nextRefresh.getTime() + WEEK_INTERVAL_MS);
  }

  return {
    lastRefresh,
    nextRefresh,
    weekStart: parsedWeekStart,
    weekEnd: parsedWeekEnd
  };
};

const DesktopAIBattery = ({
  onBatteryClick,
  showStatus = false,
  isRegenerating = false
}) => {
  const initialCycle = computeWeeklyCycle({
    currentWeekStart: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    currentWeekEnd: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000),
    lastRefreshAt: new Date(Date.now() - 24 * 60 * 60 * 1000)
  });

  const [batteryData, setBatteryData] = useState({
    percentage: 100,
    status: 'active',
    isCharging: false,
    lastUpdated: new Date(),
    weeklyQuota: 1.0,
    remainingCredits: 1.0,
    consumedCredits: 0.0,
    weekKey: null,
    nextRefresh: initialCycle.nextRefresh
  });
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const hasLoadedOnceRef = useRef(false);
  const intervalRef = useRef(null);
  const [isActive, setIsActive] = useState(false);
  const { t, language } = useLanguage();
  const { isAuthenticated, user } = useAuthContext();
  const userId = user?.id || null;

  const creditsFormatter = useMemo(() => {
    try {
      return new Intl.NumberFormat(language || 'it', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      });
    } catch (error) {
      console.warn('[DesktopAIBattery] Unable to create Intl.NumberFormat, falling back to default', error);
      return new Intl.NumberFormat('it', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      });
    }
  }, [language]);

  const formatCredits = useCallback((remainingValue, quotaValue) => {
    if (remainingValue === null || remainingValue === undefined) {
      return t('header.aiBattery.creditsUnavailable', { defaultValue: 'Crediti non disponibili' });
    }

    const remainingNumeric = toNumber(remainingValue, NaN);
    const quotaNumeric = toNumber(quotaValue, NaN);

    if (Number.isNaN(remainingNumeric)) {
      return t('header.aiBattery.creditsUnavailable', { defaultValue: 'Crediti non disponibili' });
    }

    const formattedRemaining = creditsFormatter.format(Math.max(0, remainingNumeric));

    if (!Number.isFinite(quotaNumeric) || quotaNumeric <= 0) {
      return t('header.aiBattery.creditsValue', {
        value: formattedRemaining,
        defaultValue: formattedRemaining
      });
    }

    const formattedQuota = creditsFormatter.format(quotaNumeric);
    return t('header.aiBattery.creditsWeeklyValue', {
      remaining: formattedRemaining,
      quota: formattedQuota,
      defaultValue: `${formattedRemaining}/${formattedQuota}`
    });
  }, [creditsFormatter, t]);

  const setDefaultBatteryData = useCallback(() => {
    const fallbackCycle = computeWeeklyCycle({
      currentWeekStart: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
      currentWeekEnd: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000),
      lastRefreshAt: new Date(Date.now() - 24 * 60 * 60 * 1000)
    });

    setBatteryData({
      percentage: 100,
      status: 'active',
      isCharging: false,
      lastUpdated: new Date(),
      weeklyQuota: 1.0,
      remainingCredits: 1.0,
      consumedCredits: 0.0,
      weekKey: null,
      nextRefresh: fallbackCycle.nextRefresh
    });
  }, []);

  const markFirstLoadCompleted = useCallback(() => {
    if (!hasLoadedOnceRef.current) {
      hasLoadedOnceRef.current = true;
      setHasLoadedOnce(true);
    }
  }, []);

  const fetchBatteryStatus = useCallback(async () => {
    setIsLoading((prev) => prev || !hasLoadedOnceRef.current);
    try {
      if (!isAuthenticated) {
        console.warn('[DesktopAIBattery] No auth token found, using default battery');
        setDefaultBatteryData();
        return;
      }

      const response = await apiFetch(`${API_CONFIG.BASE_URL}/api/v1/user/ai-battery`, {
        method: 'GET'
      });

      if (!response.ok) {
        console.error('[DesktopAIBattery] Failed to fetch battery status:', response.status);
        setDefaultBatteryData();
        return;
      }

      const data = await parseResponseBody(response);
      const ai = data.ai_battery || {};
      const subscriptionInfo = ai.subscription || {};

      const weeklyQuota = toNumber(ai.weekly_quota, NaN);
      const remainingCredits = toNumber(ai.remaining_credits, NaN);
      const consumedCredits = toNumber(
        ai.consumed_credits,
        Number.isFinite(weeklyQuota) && Number.isFinite(remainingCredits)
          ? weeklyQuota - remainingCredits
          : NaN
      );
      const subscriptionWeeklyCredits = toNumber(subscriptionInfo.weekly_credits, NaN);

      const cycle = computeWeeklyCycle({
        currentWeekStart: ai.current_week_start,
        currentWeekEnd: ai.current_week_end,
        nextRefreshAt: subscriptionInfo.next_refresh_at || ai.next_refresh_at,
        lastRefreshAt: ai.last_refresh_at || subscriptionInfo.last_refresh_at
      });

      const safeWeeklyQuota = Number.isFinite(weeklyQuota) && weeklyQuota > 0
        ? weeklyQuota
        : (Number.isFinite(subscriptionWeeklyCredits) && subscriptionWeeklyCredits > 0
          ? subscriptionWeeklyCredits
          : (Number.isFinite(remainingCredits) && remainingCredits > 0 ? remainingCredits : 1));

      const safeRemaining = Number.isFinite(remainingCredits)
        ? Math.max(0, remainingCredits)
        : safeWeeklyQuota;

      const derivedConsumed = Number.isFinite(consumedCredits)
        ? Math.max(0, consumedCredits)
        : Math.max(0, safeWeeklyQuota - safeRemaining);

      const consumedPercentage = toNumber(ai.consumed_percentage, NaN);
      const percentageRemaining = safeWeeklyQuota > 0
        ? Math.max(0, Math.min(100, Math.round((safeRemaining / safeWeeklyQuota) * 100)))
        : (Number.isFinite(consumedPercentage)
          ? Math.max(0, Math.min(100, Math.round(100 - consumedPercentage)))
          : 0);

      const statusLower = (ai.status || '').toLowerCase();
      let normalizedStatus = statusLower || (safeRemaining <= 0 ? 'depleted' : 'active');

      if (normalizedStatus === 'active') {
        if (safeRemaining <= 0) {
          normalizedStatus = 'depleted';
        } else if (percentageRemaining <= 20) {
          normalizedStatus = 'low';
        }
      }

      if (normalizedStatus === 'low' && safeRemaining <= 0) {
        normalizedStatus = 'depleted';
      }

      const isCharging = Boolean(
        ai.is_regenerating
        || normalizedStatus === 'refreshing'
        || normalizedStatus === 'charging'
      );

      setBatteryData({
        percentage: percentageRemaining,
        status: normalizedStatus,
        isCharging,
        lastUpdated: new Date(),
        weeklyQuota: safeWeeklyQuota,
        remainingCredits: safeRemaining,
        consumedCredits: derivedConsumed,
        weekKey: ai.week_key || null,
        nextRefresh: cycle.nextRefresh
      });
      console.log('[DesktopAIBattery] Battery status updated:', {
        percentageRemaining,
        weeklyQuota: safeWeeklyQuota,
        remainingCredits: safeRemaining,
        status: normalizedStatus
      });
    } catch (error) {
      console.error('[DesktopAIBattery] Error fetching battery status:', error);
      setDefaultBatteryData();
    } finally {
      markFirstLoadCompleted();
      setIsLoading(false);
    }
  }, [isAuthenticated, markFirstLoadCompleted, setDefaultBatteryData]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();
    intervalRef.current = setInterval(fetchBatteryStatus, 30000);
  }, [fetchBatteryStatus, stopPolling]);

  useEffect(() => {
    if (!isAuthenticated) {
      stopPolling();
      hasLoadedOnceRef.current = false;
      setHasLoadedOnce(false);
      setIsLoading(false);
      setDefaultBatteryData();
      return undefined;
    }

    fetchBatteryStatus();
    startPolling();

    return () => stopPolling();
  }, [fetchBatteryStatus, isAuthenticated, setDefaultBatteryData, startPolling, stopPolling, userId]);

  useEffect(() => {
    if (isRegenerating) {
      setIsActive(true);
      setBatteryData((prev) => ({
        ...prev,
        isCharging: true,
        status: 'refreshing'
      }));
    } else {
      setIsActive(false);
      setBatteryData((prev) => ({
        ...prev,
        isCharging: false,
        status: prev.remainingCredits <= 0
          ? 'depleted'
          : (prev.percentage < 20 && prev.status !== 'paused' ? 'low' : prev.status || 'active')
      }));
    }
  }, [isRegenerating]);

  const handleBatteryClick = () => {
    if (onBatteryClick) {
      onBatteryClick(batteryData);
    }
  };

  const getBatteryStatusText = () => {
    if (isRegenerating || batteryData.status === 'refreshing') {
      return t('header.aiBattery.status.regenerating');
    }
    if (batteryData.isCharging || batteryData.status === 'charging') {
      return t('header.aiBattery.status.charging');
    }
    if (batteryData.status === 'depleted') {
      return t('header.aiBattery.status.depleted');
    }
    if (batteryData.status === 'paused') {
      return t('header.aiBattery.status.paused', { defaultValue: 'Batteria in pausa' });
    }
    if (batteryData.status === 'critical' || batteryData.status === 'low' || batteryData.percentage < 20) {
      return t('header.aiBattery.status.low');
    }
    return t('header.aiBattery.status.ready');
  };

  const rawPercentage = toNumber(batteryData.percentage, 0);
  const chargePercentage = Math.max(0, Math.min(100, rawPercentage));
  const displayPercentage = Math.round(chargePercentage);
  const batteryLevelClass = useMemo(() => {
    if (chargePercentage <= 0 || batteryData.status === 'depleted') {
      return 'depleted';
    }
    if (chargePercentage < 20 || batteryData.status === 'low' || batteryData.status === 'critical') {
      return 'low';
    }
    if (chargePercentage < 60) {
      return 'medium';
    }
    return 'high';
  }, [batteryData.status, chargePercentage]);

  const showSkeleton = isLoading && !hasLoadedOnce;

  const batteryClasses = useMemo(() => {
    const classes = ['desktop-ai-battery'];

    if (batteryLevelClass === 'low' || batteryLevelClass === 'depleted') {
      classes.push('low-battery');
    }
    if (batteryLevelClass === 'depleted') {
      classes.push('depleted');
    }
    if (batteryData.isCharging || batteryData.status === 'charging' || isRegenerating) {
      classes.push('charging');
    }
    if (batteryData.status === 'refreshing') {
      classes.push('refreshing');
    }
    if (isActive) classes.push('active');
    if (showSkeleton) classes.push('loading', 'showing-skeleton');

    return classes.join(' ');
  }, [batteryData.isCharging, batteryData.status, batteryLevelClass, isActive, isRegenerating, showSkeleton]);

  const meterClasses = useMemo(() => (
    ['nav-battery-meter', 'nav-battery-meter-mini', batteryLevelClass]
      .filter(Boolean)
      .join(' ')
  ), [batteryLevelClass]);

  const statusText = getBatteryStatusText();
  const creditsText = formatCredits(batteryData.remainingCredits, batteryData.weeklyQuota);

  const loadingLabel = t('common.loading', { defaultValue: 'Caricamento...' });

  const titleText = showSkeleton
    ? loadingLabel
    : (showStatus
      ? `${statusText} • ${creditsText}`
      : creditsText);

  const ariaLabel = showSkeleton
    ? loadingLabel
    : t('header.aiBattery.ariaLabel', {
      percentage: displayPercentage,
      credits: creditsText
    });

  return (
    <div
      className={batteryClasses}
      onClick={handleBatteryClick}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      title={titleText}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          handleBatteryClick();
        }
      }}
      data-status={batteryData.status}
      aria-busy={showSkeleton}
    >
      {showSkeleton ? (
        <div className="desktop-ai-battery-skeleton" aria-hidden="true">
          <Skeleton
            variant="rounded"
            width="100%"
            height="100%"
            shimmer
            animation="wave"
          />
          <div className="desktop-ai-battery-skeleton-value">
            <Skeleton variant="rounded" width="100%" height={12} shimmer animation="wave" />
          </div>
        </div>
      ) : (
        <>
          <div className={meterClasses}>
            <div
              className="nav-battery-meter-fill"
              style={{ '--battery-percentage': `${chargePercentage}%` }}
            >
              <Iridescence
                className="nav-battery-iridescence"
                color={[0.706, 0.477, 0.683]}
                mouseReact={false}
                amplitude={0.06}
                speed={0.9}
                aria-hidden="true"
              />
            </div>
            <div className="nav-battery-meter-outline" aria-hidden="true" />
            <div className="nav-battery-meter-value">
              <span>{displayPercentage}</span>
            </div>
          </div>
          {isLoading && <div className="desktop-battery-loading" />}
        </>
      )}
    </div>
  );
};

export default DesktopAIBattery;
